import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { Label } from '@neo/primitives/label'
import React from 'react'
import type { z } from 'zod'
import { AuthType, eventCallbacksSchema } from '../../config/agent-settings.validation.config'
import type { AgentSettingsFormValues } from '../../config/base.config'
import type { EventCallbackFieldPath } from './event-callback-field'

export type EventCallbackKey = keyof z.infer<typeof eventCallbacksSchema>

interface AuthConfigProps {
  fieldPath: EventCallbackFieldPath
  effectiveShowAuth: boolean
  form: GenericFormType<AgentSettingsFormValues>
}

export const AuthConfig = React.memo(({ fieldPath, effectiveShowAuth, form }: AuthConfigProps) => {
  if (!effectiveShowAuth) return null

  return (
    <div className="flex mt-2">
      <div className="flex flex-col ml-4 -mt-4">
        <div className="border-l border-dashed border-input h-[50px] w-0" />
        <div className="border-t border-dashed border-input  w-8 h-0 ml-[-1px]" />
      </div>
      <div className="rounded-md p-4 border flex flex-col gap-4 mt-0 w-full">
        <Label>Auth type</Label>
        <FieldsRenderer<AgentSettingsFormValues>
          form={form}
          fields={[
            {
              name: `${fieldPath}.auth`,
              componentType: 'select',
              inputProps: {
                options: [
                  { label: 'Basic Auth', value: AuthType.BASIC_AUTH },
                  { label: 'Bearer Token', value: AuthType.BEARER_TOKEN },
                ],
                placeholder: 'Select Auth Type',
              },
              zodValidation:
                eventCallbacksSchema.shape[
                  fieldPath.split('.')[1] as keyof typeof eventCallbacksSchema.shape
                ].shape.auth,
            },
            {
              name: `${fieldPath}.username`,
              componentType: 'input',
              onChangeListenTo: [`${fieldPath}.auth`],
              visible: (values: AgentSettingsFormValues) =>
                values.event_callbacks?.[fieldPath.split('.')[1] as EventCallbackKey]?.auth ===
                AuthType.BASIC_AUTH,
              inputProps: { placeholder: 'Username' },
              zodValidation:
                eventCallbacksSchema.shape[
                  fieldPath.split('.')[1] as keyof typeof eventCallbacksSchema.shape
                ].shape.username,
            },
            {
              name: `${fieldPath}.password`,
              componentType: 'inputPassword',
              onChangeListenTo: [`${fieldPath}.auth`],
              visible: (values: AgentSettingsFormValues) =>
                values.event_callbacks?.[fieldPath.split('.')[1] as EventCallbackKey]?.auth ===
                AuthType.BASIC_AUTH,
              inputProps: { placeholder: 'Password' },
              zodValidation:
                eventCallbacksSchema.shape[
                  fieldPath.split('.')[1] as keyof typeof eventCallbacksSchema.shape
                ].shape.password,
            },
            {
              name: `${fieldPath}.token`,
              componentType: 'input',
              onChangeListenTo: [`${fieldPath}.auth`],
              visible: (values: AgentSettingsFormValues) =>
                values.event_callbacks?.[fieldPath.split('.')[1] as EventCallbackKey]?.auth ===
                AuthType.BEARER_TOKEN,
              inputProps: { placeholder: 'Token' },
              zodValidation:
                eventCallbacksSchema.shape[
                  fieldPath.split('.')[1] as keyof typeof eventCallbacksSchema.shape
                ].shape.token,
            },
          ]}
        />
      </div>
    </div>
  )
})

export default AuthConfig
